import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { router } from "@inertiajs/react"
import { useState } from "react"
import { type Column, PRIORITY_LABELS, type Person, type Priority } from "../types"

const UNASSIGNED = "unassigned"

interface NewTicketDialogProps {
  boardKey: string
  column: Column
  assignableUsers: Person[]
  onOpenChange: (open: boolean) => void
}

export function NewTicketDialog({ boardKey, column, assignableUsers, onOpenChange }: NewTicketDialogProps) {
  const [title, setTitle] = useState("")
  const [priority, setPriority] = useState<Priority>("normal")
  const [assignee, setAssignee] = useState(UNASSIGNED)

  const submit = () => {
    if (!title.trim()) return

    router.post(
      `/boards/${boardKey}/tickets`,
      {
        ticket: {
          title,
          priority,
          board_column_id: column.id,
          assignee_id: assignee === UNASSIGNED ? null : Number(assignee),
        },
      },
      { preserveScroll: true, onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New ticket in {column.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="ticket_title">Title</Label>
          <Input
            id="ticket_title"
            value={title}
            autoFocus
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs doing?"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="ticket_priority">Priority</Label>
            <Select value={priority} onValueChange={(value) => setPriority(value as Priority)}>
              <SelectTrigger id="ticket_priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ticket_assignee">Assignee</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger id="ticket_assignee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Nobody yet</SelectItem>
                {assignableUsers.map((user) => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!title.trim()}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
